// src/screens/SelectAddress.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PrimaryButton } from "../components/Button";
import Navbar from "../components/Navbar";
import PageTitle from "../components/PageTitle";
import { getUserAddresses } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

type AddressItem = {
    _id?: string;
    label: string;
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    landmark?: string;
};

const SelectAddress: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { state } = location;

    // Get booking data from navigation state (trainer, slot, time, day)
    const bookingData = state || {};

    const [addresses, setAddresses] = useState<AddressItem[]>([]);
    const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch addresses on component mount
    useEffect(() => {
        const fetchAddresses = async () => {
            if (!user?._id) return;

            try {
                setLoading(true);
                setError(null);
                const response = await getUserAddresses(user._id);
                const fetchedAddresses = response.addresses || [];
                setAddresses(fetchedAddresses);

                // Auto-select first address if available
                if (fetchedAddresses.length > 0 && !selectedAddressId) {
                    setSelectedAddressId(fetchedAddresses[0]._id || null);
                }
            } catch (err) {
                console.error('Error fetching addresses:', err);
                setError('Failed to load addresses');
            } finally {
                setLoading(false);
            }
        };

        fetchAddresses();
    }, [user?._id]);

    const handleAddressSelect = (addressId: string) => {
        setSelectedAddressId(addressId);
    };

    const handleContinue = () => {
        if (bookingData?.trainer?.isGym) {
            const gymAddress = bookingData.trainer.fullTrainerData?.address || "Address not specified";
            navigate('/student/session-duration', {
                state: {
                    ...bookingData,
                    selectedAddress: bookingData.trainer.name,
                    address: gymAddress,
                    addressData: { label: bookingData.trainer.name, street: gymAddress }
                }
            });
            return;
        }

        if (!selectedAddressId) {
            setError('Please select an address');
            return;
        }

        const selectedAddress = addresses.find(addr => addr._id === selectedAddressId);
        if (!selectedAddress) {
            setError('Selected address not found');
            return;
        }

        // Format address string
        const addressParts = [];
        const excludeValues = ['Not specified', 'City', 'State', 'Country', 'Address'];
        if (selectedAddress.street && !excludeValues.includes(selectedAddress.street)) {
            addressParts.push(selectedAddress.street);
        }
        if (selectedAddress.landmark && selectedAddress.landmark.trim()) {
            addressParts.push(`Near ${selectedAddress.landmark}`);
        }
        if (selectedAddress.city && !excludeValues.includes(selectedAddress.city)) {
            addressParts.push(selectedAddress.city);
        }
        if (selectedAddress.state && !excludeValues.includes(selectedAddress.state)) {
            addressParts.push(selectedAddress.state);
        }
        if (selectedAddress.postalCode && !excludeValues.includes(selectedAddress.postalCode)) {
            addressParts.push(selectedAddress.postalCode);
        }
        if (selectedAddress.country && !excludeValues.includes(selectedAddress.country)) {
            addressParts.push(selectedAddress.country);
        }
        const formattedAddress = addressParts.length > 0 ? addressParts.join(', ') : 'Address not specified';

        // Navigate to session duration with selected address and booking data
        navigate('/student/session-duration', {
            state: {
                ...bookingData,
                selectedAddress: selectedAddress.label,
                address: formattedAddress,
                addressData: selectedAddress,
            }
        });
    };

    const handleAddNewAddress = () => {
        navigate("/student/set-location", {
            state: {
                from: "/student/select-address",
                ...bookingData, // Preserve booking data
            }
        });
    };

    return (
        <div
            className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto"
            style={{ WebkitOverflowScrolling: "touch" }}
        >
            <div className="w-full min-h-full flex flex-col">
                {/* NAVBAR */}
                <Navbar
                    onBack={() => navigate(-1)}
                    background="transparent"
                    spacerHeight={40}
                />

                {/* CONTENT */}
                <main className="flex-1 px-5">
                    <div className="pt-4">
                        <PageTitle>Select the address where the trainer should come and train you</PageTitle>

                        {/* LOADING STATE */}
                        {loading && (
                            <div className="flex justify-center items-center py-8">
                                <div className="text-[#717171]">Loading addresses...</div>
                            </div>
                        )}

                        {/* ERROR STATE */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                                {error}
                            </div>
                        )}

                        {/* ADDRESS CARDS */}
                        {!loading && !error && bookingData?.trainer?.isGym ? (
                            <div className="bg-white border px-4 py-4 border-[#D62422] border-2 mb-10">
                                <div className="text-[12px] font-extrabold tracking-[0.02em] uppercase text-[#D62422]">
                                    {bookingData.trainer.name}
                                </div>
                                <div className="mt-1 text-[14px] leading-5 text-[#717171]">
                                    {bookingData.trainer.fullTrainerData?.address || "Address not specified"}
                                </div>
                                <div className="mt-2 flex items-center">
                                    <svg className="w-4 h-4 text-[#D62422] mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-[12px] font-extrabold text-[#D62422] uppercase">Selected</span>
                                </div>
                            </div>
                        ) : !loading && !error && (
                            <div className="space-y-4 mb-10">
                                {addresses.length === 0 ? (
                                    <div className="text-center py-8 text-[#717171]">
                                        No saved addresses yet
                                    </div>
                                ) : (
                                    addresses.map((item) => {
                                        const isSelected = selectedAddressId === item._id;
                                        return (
                                            <div
                                                key={item._id}
                                                onClick={() => handleAddressSelect(item._id!)}
                                                className={`bg-white border px-4 py-4 cursor-pointer transition-colors ${isSelected
                                                    ? 'border-[#D62422] border-2'
                                                    : 'border-[#E1E1E1]'
                                                    }`}
                                            >
                                                <div className={`text-[12px] font-extrabold tracking-[0.02em] uppercase ${isSelected ? 'text-[#D62422]' : 'text-[#121212]'
                                                    }`}>
                                                    {item.label}
                                                </div>
                                                <div className="mt-1 text-[14px] leading-5 text-[#717171]">
                                                    {(() => {
                                                        const parts = [];
                                                        // Filter out empty strings, "Not specified", and generic placeholders
                                                        const excludeValues = ['Not specified', 'City', 'State', 'Country', 'Address'];
                                                        if (item.street && !excludeValues.includes(item.street)) parts.push(item.street);
                                                        if (item.landmark && item.landmark.trim()) parts.push(`Near ${item.landmark}`);
                                                        if (item.city && !excludeValues.includes(item.city)) parts.push(item.city);
                                                        if (item.state && !excludeValues.includes(item.state)) parts.push(item.state);
                                                        if (item.postalCode && !excludeValues.includes(item.postalCode)) parts.push(item.postalCode);
                                                        if (item.country && !excludeValues.includes(item.country)) parts.push(item.country);
                                                        return parts.length > 0 ? parts.join(', ') : 'Address details not available';
                                                    })()}
                                                </div>
                                                {isSelected && (
                                                    <div className="mt-2 flex items-center">
                                                        <svg
                                                            className="w-4 h-4 text-[#D62422] mr-2"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                        <span className="text-[12px] font-extrabold text-[#D62422] uppercase">
                                                            Selected
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        {/* ADD NEW ADDRESS BUTTON */}
                        {!bookingData?.trainer?.isGym && (
                        <div className="mb-10">
                            <button
                                onClick={handleAddNewAddress}
                                className="w-full bg-white border border-[#E1E1E1] px-4 py-3 flex items-center justify-center gap-2 text-[#D62422] font-extrabold uppercase text-[12px] tracking-[0.02em] hover:bg-gray-50 transition-colors"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M12 4v16m8-8H4"
                                    />
                                </svg>
                                <span>ADD NEW ADDRESS</span>
                            </button>
                        </div>
                        )}
                    </div>
                </main>

                {/* CONTINUE BUTTON - Fixed at bottom */}
                <div className="px-5 pb-[max(16px,env(safe-area-inset-bottom))] pt-4 bg-[#F7F7F7] border-t border-[#E1E1E1]">
                    <PrimaryButton
                        label="SELECT SESSION DURATION"
                        onClick={handleContinue}
                        disabled={!bookingData?.trainer?.isGym && (!selectedAddressId || addresses.length === 0)}
                        className="!w-full"
                    />
                </div>
            </div>
        </div>
    );
};

export default SelectAddress;
